"use client"

import React, { useState, useEffect } from "react"
import { motion, type Variants } from "framer-motion"
import { Check, ChevronRight } from "lucide-react"

// Custom hook for responsive viewport detection
const useResponsiveOrientation = (defaultOrientation: "horizontal" | "vertical") => {
  const [isMobile, setIsMobile] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    
    // Check initial viewport
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint in Tailwind
    }
    
    checkMobile()
    
    // Add resize listener
    const handleResize = () => {
      checkMobile()
    }
    
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // If not mounted or user explicitly set orientation, use default
  if (!mounted) return defaultOrientation
  
  // On mobile, prefer vertical unless explicitly set to horizontal
  if (isMobile && defaultOrientation === "horizontal") {
    return "vertical"
  }
  
  return defaultOrientation
}

export interface StepperStep {
  id: string | number
  label: string
  description?: string
  icon?: React.ReactNode
  content?: React.ReactNode
}

export interface StepperProps {
  steps: StepperStep[]
  activeStep?: number
  onStepChange?: (stepIndex: number) => void
  orientation?: "horizontal" | "vertical"
  variant?: "default" | "compact" | "detailed"
  clickable?: boolean
  showContent?: boolean
  size?: "sm" | "md" | "lg"
  className?: string
}

const Stepper = React.forwardRef<HTMLDivElement, StepperProps>(
  (
    {
      steps,
      activeStep = 0,
      onStepChange,
      orientation = "horizontal",
      variant = "default",
      clickable = true,
      showContent = true,
      size = "md",
      className = "",
    },
    ref
  ) => {
    const [currentStep, setCurrentStep] = useState(activeStep)
    const responsiveOrientation = useResponsiveOrientation(orientation)

    const handleStepClick = (index: number) => {
      if (clickable) {
        setCurrentStep(index)
        onStepChange?.(index)
      }
    }

    const isStepCompleted = (index: number) => index < currentStep
    const isStepActive = (index: number) => index === currentStep

    // Size configurations
    const sizeConfig = {
      sm: {
        stepSize: "w-8 h-8",
        fontSize: "text-xs md:text-sm",
        iconSize: "w-4 h-4",
        spacing: "gap-2 md:gap-3",
      },
      md: {
        stepSize: "w-10 h-10",
        fontSize: "text-sm md:text-base",
        iconSize: "w-5 h-5",
        spacing: "gap-3 md:gap-4",
      },
      lg: {
        stepSize: "w-12 h-12",
        fontSize: "text-base md:text-lg",
        iconSize: "w-6 h-6",
        spacing: "gap-4 md:gap-6",
      },
    }

    const config = sizeConfig[size]

    const stepVariants: Variants = {
      hidden: { opacity: 0, scale: 0.8 },
      visible: (i: number) => ({
        opacity: 1,
        scale: 1,
        transition: {
          delay: i * 0.1,
          type: "spring",
          stiffness: 100,
        },
      }),
    }

    const contentVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3 },
      },
      exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
    }

    const connectorVariants: Variants = {
      hidden: { scaleX: 0, opacity: 0 },
      visible: (i: number) => ({
        scaleX: 1,
        opacity: isStepCompleted(i + 1) ? 1 : 0.3,
        transition: {
          delay: (i + 1) * 0.1,
          duration: 0.4,
        },
      }),
    }

    if (responsiveOrientation === "vertical") {
      return (
        <div
          ref={ref}
          className={`w-full ${className}`}
        >
          <div className="space-y-6 md:space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.id}
                custom={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={stepVariants}
                className="relative"
              >
                {/* Connection line for non-last items */}
                {index < steps.length - 1 && (
                  <div className="absolute left-[calc(1.25rem_+_0.5px)] top-12 h-12 w-0.5 bg-gradient-to-b from-primary/60 to-primary/20" />
                )}

                <div className="flex gap-4 md:gap-6">
                  {/* Step indicator */}
                  <div className="shrink-0 pt-1">
                    <motion.button
                      onClick={() => handleStepClick(index)}
                      disabled={!clickable}
                      whileHover={clickable ? { scale: 1.1 } : {}}
                      whileTap={clickable ? { scale: 0.95 } : {}}
                      className={`relative flex items-center justify-center rounded-full font-semibold transition-all ${config.stepSize} ${
                        isStepCompleted(index)
                          ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg shadow-primary/30"
                          : isStepActive(index)
                          ? "bg-gradient-to-br from-primary to-secondary text-white shadow-xl shadow-primary/40 ring-2 ring-primary/40"
                          : "bg-white/[0.08] border border-white/20 text-white/70 hover:bg-white/[0.12] hover:border-white/30"
                      } ${config.fontSize} ${clickable && !isStepActive(index) ? "cursor-pointer" : ""}`}
                    >
                      {isStepCompleted(index) ? (
                        <Check className={config.iconSize} strokeWidth={3} />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </motion.button>
                  </div>

                  {/* Step content */}
                  <div className="flex-1 min-w-0 pt-1">
                    <motion.div
                      initial={false}
                      animate={{
                        opacity: isStepActive(index) ? 1 : 0.7,
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <h3
                        className={`font-semibold ${config.fontSize} ${
                          isStepActive(index)
                            ? "text-white"
                            : "text-white/70"
                        } transition-colors`}
                      >
                        {step.label}
                      </h3>
                      {step.description && (
                        <p className="text-xs md:text-sm text-white/50 mt-1">
                          {step.description}
                        </p>
                      )}
                    </motion.div>

                    {/* Expandable content */}
                    {showContent && isStepActive(index) && step.content && (
                      <motion.div
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={contentVariants}
                        className="mt-4 p-4 md:p-6 rounded-xl bg-white/[0.04] border border-white/10 backdrop-blur-sm"
                      >
                        {step.content}
                      </motion.div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )
    }

    // Horizontal orientation
    return (
      <div ref={ref} className={`w-full ${className}`}>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={{
            visible: {
              transition: { staggerChildren: 0.1 },
            },
          }}
          className="space-y-8 md:space-y-12"
        >
          {/* Steps container */}
          <div
            className={`flex items-center justify-between overflow-hidden py-2 ${config.spacing}`}
          >
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                {/* Step */}
                <motion.div
                  custom={index}
                  variants={stepVariants}
                  className="flex flex-col items-center shrink-0"
                >
                  <motion.button
                    onClick={() => handleStepClick(index)}
                    disabled={!clickable}
                    whileHover={clickable ? { scale: 1.15 } : {}}
                    whileTap={clickable ? { scale: 0.9 } : {}}
                    className={`flex items-center justify-center rounded-full font-bold transition-all mb-2 ${config.stepSize} ${
                      isStepCompleted(index)
                        ? "bg-gradient-to-br from-primary to-primary/80 text-white shadow-lg shadow-primary/30"
                        : isStepActive(index)
                        ? "bg-gradient-to-br from-primary to-secondary text-white shadow-xl shadow-primary/40 ring-2 ring-primary/40"
                        : "bg-white/[0.08] border border-white/20 text-white/70 hover:bg-white/[0.12] hover:border-white/30"
                    } ${config.fontSize} ${clickable && !isStepActive(index) ? "cursor-pointer" : ""}`}
                  >
                    {isStepCompleted(index) ? (
                      <Check className={config.iconSize} strokeWidth={3} />
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </motion.button>

                  {/* Label - Hide on mobile for compact view, show on tablet+ */}
                  <div className="text-center min-w-max px-1">
                    <p
                      className={`font-medium ${config.fontSize} ${
                        isStepActive(index)
                          ? "text-white"
                          : "text-white/70"
                      } hidden sm:block transition-colors`}
                    >
                      {variant === "compact"
                        ? step.label.split(" ")[0]
                        : step.label}
                    </p>
                    {step.description && variant === "detailed" && (
                      <p className="text-xs text-white/50 mt-1 hidden md:block">
                        {step.description}
                      </p>
                    )}
                  </div>
                </motion.div>

                {/* Connector line - Only show between steps, and hide on mobile if too many steps */}
                {index < steps.length - 1 && steps.length <= 5 && (
                  <motion.div
                    custom={index}
                    variants={connectorVariants}
                    className={`h-1 flex-1 min-w-4 md:min-w-6 rounded-full origin-left transition-all ${
                      isStepCompleted(index + 1)
                        ? "bg-gradient-to-r from-primary to-primary/60"
                        : "bg-white/10"
                    }`}
                  />
                )}

                {/* Compact connector for many steps */}
                {index < steps.length - 1 && steps.length > 5 && (
                  <ChevronRight className="w-5 h-5 text-white/30 shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Mobile-friendly label display for horizontal */}
          <div className="block sm:hidden">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={contentVariants}
              className="text-center"
            >
              <p className="text-sm font-semibold text-white">
                {steps[currentStep]?.label}
              </p>
              {steps[currentStep]?.description && (
                <p className="text-xs text-white/60 mt-1">
                  {steps[currentStep].description}
                </p>
              )}
            </motion.div>
          </div>

          {/* Content Display */}
          {showContent && steps[currentStep]?.content && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={contentVariants}
              className="p-4 md:p-8 rounded-2xl bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 backdrop-blur-md shadow-xl"
            >
              {steps[currentStep].content}
            </motion.div>
          )}
        </motion.div>
      </div>
    )
  }
)

Stepper.displayName = "Stepper"

export default Stepper
